import { Trash2 } from "lucide-react";
import { ACCENT_CLASS } from "@/constants/conditionCategories";
import { MEMBERSHIP_CATEGORIES, membershipFields } from "./conditionModel";
import {
  TagCategoryBody,
  MembershipFieldsBody,
  BehaviorCategoryBody,
  FieldsCategoryBody,
} from "./CategoryBodies";
import { AnalyticsCategoryBody } from "@/features/analytics/components/filters/AnalyticsCategoryBody";

export default function CategoryCard({
  category,
  conditions,
  combinator,
  activeTab,
  fieldOptionConfig,
  onSetConditions,
  onRemoveCategory,
}) {
  const Icon = category.icon;
  // Analytics categories (from buildAnalyticsCategories) carry per-field render
  // descriptors and render through the analytics-aware body — WITHOUT touching
  // SEGMENT_FIELDS. Segment categories never set `fieldDescriptors`, so this
  // gate leaves the segment rendering path byte-identical.
  const isAnalytics = Boolean(category.fieldDescriptors);
  const isMembership = !isAnalytics && MEMBERSHIP_CATEGORIES.has(category.id);
  const inline = !isAnalytics && category.id === "interests";

  let body;
  if (isAnalytics) {
    body = (
      <AnalyticsCategoryBody
        category={category}
        conditions={conditions}
        onSetConditions={onSetConditions}
      />
    );
  } else if (isMembership) {
    body = (
      <MembershipFieldsBody
        fields={membershipFields(category.id, fieldOptionConfig)}
        conditions={conditions}
        onSetConditions={onSetConditions}
      />
    );
  } else if (category.id === "behavior") {
    body = (
      <BehaviorCategoryBody
        conditions={conditions}
        onSetConditions={onSetConditions}
      />
    );
  } else if (category.kind === "tags") {
    body = (
      <TagCategoryBody
        category={category}
        conditions={conditions}
        combinator={combinator}
        activeTab={activeTab}
        onSetConditions={onSetConditions}
      />
    );
  } else {
    body = (
      <FieldsCategoryBody
        category={category}
        conditions={conditions}
        fieldOptionConfig={fieldOptionConfig}
        onSetConditions={onSetConditions}
      />
    );
  }

  return (
    <div className="rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-1)">
      <div className="flex items-center gap-3 px-4 py-3">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-(--radius-md) ${
            ACCENT_CLASS[category.accent] ?? ACCENT_CLASS.slate
          }`}
        >
          <Icon size={16} strokeWidth={2} />
        </span>
        <span className="shrink-0 text-sm font-semibold text-(--text-primary)">
          {category.label}
        </span>
        {inline && <div className="min-w-0 flex-1">{body}</div>}
        <button
          type="button"
          onClick={onRemoveCategory}
          className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-(--radius-md) text-(--text-muted) hover:bg-(--surface-2) hover:text-(--status-danger)"
          aria-label={`Remove ${category.label}`}
        >
          <Trash2 size={15} />
        </button>
      </div>
      {!inline && (
        <div className="border-t border-(--border-subtle) px-4 py-3">
          {body}
        </div>
      )}
    </div>
  );
}
